import type { JSX } from "react";
import { Link } from "react-router";
import { routePaths } from "../../app/navigation";
import { hasAccessRight } from "../../shared/auth/accessRights";
import { useI18n } from "../../shared/i18n/I18nContext";
import { useMe } from "../../shared/session/useMe";
import { cx } from "../../shared/ui/classNames";
import styles from "../../shared/ui/ui.module.css";
import { setupWizardProgress } from "./model/setupWizard";
import { useSetupChecklist } from "./useSetup";

/**
 * Shows HR on the start screen how far the setup of the tenant is (T-133, "Checkliste im
 * Dashboard"), counted in the steps of the assistant (F-72), until every step the server reports
 * items for is done (F-85). Renders nothing for everybody else.
 *
 * @returns The banner or nothing.
 */
export function SetupBanner(): JSX.Element | null {
  const { t } = useI18n();
  const me = useMe();
  const isHr = hasAccessRight(me.data?.roles ?? [], "ManageMasterData");
  const checklist = useSetupChecklist(isHr);
  const progress = setupWizardProgress(checklist.data ?? []);

  // Only steps the server reports items for count (F-85): nothing reported, nothing to show.
  if (!isHr || progress.total === 0 || progress.complete) {
    return null;
  }

  const banner = (
    <aside className={cx(styles["card"], styles["row"])} aria-label={t("setup.checklist")}>
      <span>
        {t("setup.progress", { done: String(progress.done), total: String(progress.total) })}
      </span>
      <Link className={styles["button"]} to={routePaths.setup}>
        {t("setup.continue")}
      </Link>
    </aside>
  );

  return banner;
}
